import {ChangeDetectorRef, Component, OnInit, ViewContainerRef} from '@angular/core';
import {BusinessRulesService} from '../../legacy-business-rules/shared/business-rules.service';
import {ConfigurationService} from '../configuration.service';
import {NotificationPopupService} from '../../shared/notification-popup/services/notification-popup.service';

@Component({
    selector: 'app-yield-protection',
    templateUrl: './yield-protection.component.html',
    styleUrls: ['./yield-protection.component.scss']
})
export class YieldProtectionComponent implements OnInit {
    // Master enable toggle
    public yieldProtectionEnabled = false;

    // Days Prior (mirrors the Business Rules days prior selector)
    public yieldProtectionDaysPriorData: any[] = [];
    public yieldProtectionSelectedDaysPrior: number;

    // Services/Market Exclusion (mirrors the Business Rules Services/Markets selector)
    public yieldProtectionRsidData: any[] = [];
    public yieldProtectionRsidDataAllValues: any[] = [];
    public yieldProtectionSelectedRsids: any[] = [];
    // Switch on (right) = Exclusive, off (left) = Inclusive
    public yieldProtectionServicesExclusive = false;

    // Each independently-loaded piece this screen needs, tracked separately since any one
    // of the three requests can settle first. The section stays hidden behind a spinner
    // until all three have, so nothing shows an empty dropdown or a false "off" before its
    // real value has actually arrived.
    private daysPriorLoaded = false;
    private rsidsLoaded = false;
    private savedConfigLoaded = false;
    public get loaded(): boolean {
        return this.daysPriorLoaded && this.rsidsLoaded && this.savedConfigLoaded;
    }

    public saving = false;

    constructor(private businessRulesSvc: BusinessRulesService,
                private configurationSvc: ConfigurationService,
                private notificationPopupSvc: NotificationPopupService,
                private viewContainerRef: ViewContainerRef,
                private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.loadDaysPrior();
        this.loadRSIDs();
        this.loadSavedConfiguration();
    }

    // Prepopulates the screen from the previously saved yield_protection set. Applied
    // defensively so missing/partial config falls back to defaults.
    private loadSavedConfiguration(): void {
        this.configurationSvc.getConfiguration().subscribe(config => {
            const yieldProtection = config?.yield_protection;
            if (yieldProtection) {
                this.yieldProtectionEnabled = yieldProtection.enabled ?? this.yieldProtectionEnabled;
                this.yieldProtectionSelectedDaysPrior = yieldProtection.daysPrior ?? this.yieldProtectionSelectedDaysPrior;
                this.yieldProtectionSelectedRsids = yieldProtection.services ?? this.yieldProtectionSelectedRsids;
                this.yieldProtectionServicesExclusive = yieldProtection.servicesExclusive ?? this.yieldProtectionServicesExclusive;
            }
            this.savedConfigLoaded = true;
            this.cdr.detectChanges();
        }, error => {
            console.error(`Error retrieving saved configuration ${error}`);
            this.savedConfigLoaded = true;
            this.cdr.detectChanges();
        });
    }

    private loadDaysPrior(): void {
        this.businessRulesSvc.getDaysPrior().subscribe(results => {
            this.yieldProtectionDaysPriorData = results;
            this.daysPriorLoaded = true;
            this.cdr.detectChanges();
        }, error => {
            console.error(`Error retrieving days prior data ${error}`);
            this.daysPriorLoaded = true;
            this.cdr.detectChanges();
        });
    }

    private loadRSIDs(): void {
        this.businessRulesSvc.getRSIDs().subscribe(result => {
            this.yieldProtectionRsidData = result;
            this.yieldProtectionRsidDataAllValues = result;
            this.rsidsLoaded = true;
            this.cdr.detectChanges();
        }, error => {
            console.error(`Error retrieving RSID data ${error}`);
            this.rsidsLoaded = true;
            this.cdr.detectChanges();
        });
    }

    public handleFilterRSID(value: string): void {
        if (value && value.trim().length) {
            // Split the values on both comma and space.
            const terms = value.split(/[\s,]+/).map(x => x.toUpperCase()).filter(x => x.length);
            if (terms.length > 1) {
                this.yieldProtectionRsidData = this.yieldProtectionRsidDataAllValues
                    .filter(s => terms.some(t => s.toUpperCase().includes(t)));
            } else {
                this.yieldProtectionRsidData = this.yieldProtectionRsidDataAllValues
                    .filter(s => s.toLowerCase().indexOf(value.toLowerCase()) !== -1);
            }
        } else {
            this.yieldProtectionRsidData = this.yieldProtectionRsidDataAllValues;
        }
        this.cdr.detectChanges();
    }

    public selectAll(multiSelect: any): void {
        this.yieldProtectionSelectedRsids = [...this.yieldProtectionRsidDataAllValues];
        multiSelect.writeValue(this.yieldProtectionSelectedRsids);
        this.cdr.detectChanges();
    }

    public clearAll(multiSelect: any): void {
        this.yieldProtectionSelectedRsids = [];
        multiSelect.writeValue(this.yieldProtectionSelectedRsids);
        this.cdr.detectChanges();
    }

    public onSave(): void {
        const payload = {
            yield_protection: {
                enabled: this.yieldProtectionEnabled,
                daysPrior: this.yieldProtectionSelectedDaysPrior,
                services: this.yieldProtectionSelectedRsids,
                servicesExclusive: this.yieldProtectionServicesExclusive
            }
        };

        this.saving = true;
        this.configurationSvc.saveConfiguration(payload).subscribe(() => {
            this.notificationPopupSvc.displayPageNotification(this.viewContainerRef, {
                notificationType: 'success',
                message: 'Configuration saved.'
            });
            this.saving = false;
        }, error => {
            console.error(`Error saving configuration ${error}`);
            this.notificationPopupSvc.displayPageNotification(this.viewContainerRef, {
                notificationType: 'error',
                message: 'Failed to save configuration.'
            });
            this.saving = false;
        });
    }
}
